import { tradingClient } from "@/shared/rpc/clients";
import { userStore } from "@/shared/stores/UserStore";
import {
  TradingProfileAccountScope,
  TradingProfilePeriodPreset,
  type TradingProfileAnalyticsMessage,
} from "@cronika/proto/client/trading_pb";
import { makeAutoObservable, runInAction } from "mobx";

class TradingProfileStore {
  accountScope: TradingProfileAccountScope = TradingProfileAccountScope.REAL;
  periodPreset: TradingProfilePeriodPreset = TradingProfilePeriodPreset.DAY;
  /** ISO strings — only used when `periodPreset === CUSTOM` */
  customDateFrom?: string;
  customDateTo?: string;

  data: TradingProfileAnalyticsMessage | null = null;
  loading = false;
  loadFailed = false;

  private loadGeneration = 0;

  constructor() {
    makeAutoObservable(this);
  }

  resetForNewSession() {
    runInAction(() => {
      this.accountScope = TradingProfileAccountScope.REAL;
      this.periodPreset = TradingProfilePeriodPreset.DAY;
      this.customDateFrom = undefined;
      this.customDateTo = undefined;
      this.data = null;
      this.loadFailed = false;
    });
  }

  private async fetchAnalytics(): Promise<TradingProfileAnalyticsMessage> {
    const tz = userStore.user?.timezone || "UTC";
    return tradingClient.getTradingProfileAnalytics({
      accountScope: this.accountScope,
      periodPreset: this.periodPreset,
      dateFrom: this.periodPreset === TradingProfilePeriodPreset.CUSTOM ? this.customDateFrom : undefined,
      dateTo: this.periodPreset === TradingProfilePeriodPreset.CUSTOM ? this.customDateTo : undefined,
      timeZone: tz,
    });
  }

  resetPeriodPreset() {
    this.periodPreset = TradingProfilePeriodPreset.DAY;
    this.customDateFrom = undefined;
    this.customDateTo = undefined;
  }

  setPeriodPreset(p: TradingProfilePeriodPreset) {
    this.periodPreset = p;
    if (p !== TradingProfilePeriodPreset.CUSTOM) {
      this.customDateFrom = undefined;
      this.customDateTo = undefined;
    }
    void this.load();
  }

  setCustomRange(from?: string, to?: string) {
    this.customDateFrom = from;
    this.customDateTo = to;
    this.periodPreset = TradingProfilePeriodPreset.CUSTOM;
    if (from && to) void this.load();
  }

  clearCustomRange() {
    this.customDateFrom = undefined;
    this.customDateTo = undefined;
    this.periodPreset = TradingProfilePeriodPreset.DAY;
    void this.load();
  }

  setAccountScope(next: TradingProfileAccountScope) {
    this.accountScope = next;
    void this.load();
  }

  async load() {
    if (this.periodPreset === TradingProfilePeriodPreset.CUSTOM) {
      if (!this.customDateFrom || !this.customDateTo) return;
    }

    const generation = ++this.loadGeneration;
    this.loading = true;
    this.loadFailed = false;

    try {
      const response = await this.fetchAnalytics();
      if (generation !== this.loadGeneration) return;

      runInAction(() => {
        if (generation !== this.loadGeneration) return;
        this.data = response;
        this.loading = false;
      });
    } catch (_err: unknown) {
      runInAction(() => {
        if (generation !== this.loadGeneration) return;
        this.loading = false;
        this.loadFailed = true;
      });
    }
  }
}

export const tradingProfileStore = new TradingProfileStore();
